import "../../kendo.data.js"; // Stiil required until kendo.data is modularized
import { IKendoJQueryStatic } from "../../core/index.js";
import { utilsService } from "../../core/services/utils.service.js";
import type { IMessage, IChatOptions, IMessageReferenceData } from "../models/index.js";
import type { IFile } from "../../upload";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

/**
 * Data source schema configuration for chat messages.
 */
interface IDataSourceSchema {
    model: {
        id: string;
        fields: Record<string, {
            type?: string;
            defaultValue?: any;
            from?: string;
            parse?: (value: any) => any;
        }>;
    };
}

/**
 * Internal options for the data collaborator.
 */
interface IDataManagerOptions {
    autoSync: boolean;
    autoAssignId?: boolean;
    schema: IDataSourceSchema;
    data?: any[];
}

export interface IChatDataContext {
    chatOptions: Partial<IChatOptions>;
}

class AuxiliaryReferenceStore {
    private pinnedMessages: IMessageReferenceData[];
    private resolvedReferenceMessages: IMessageReferenceData[] = [];

    constructor(pinnedMessages: IMessageReferenceData[]) {
        this.pinnedMessages = pinnedMessages;
    }

    getMessageById(id: string | number): IMessageReferenceData | null {
        if (!id) {
            return null;
        }

        return this._getPinnedMessageById(id) || this._getResolvedReferenceMessageById(id);
    }

    getPinnedMessage(): IMessageReferenceData | null {
        for (let index = this.pinnedMessages.length - 1; index >= 0; index--) {
            if (this.pinnedMessages[index].isPinned) {
                return this.pinnedMessages[index];
            }
        }

        return this.pinnedMessages[this.pinnedMessages.length - 1] || null;
    }

    syncPinnedMessageState(
        message: IMessageReferenceData | null,
        isPinned: boolean,
        setPinnedState: (message: IMessageReferenceData | null, value: boolean) => void
    ): void {
        const auxiliaryPinnedMessage = this._getPinnedMessageById(message?.id);

        if (auxiliaryPinnedMessage && auxiliaryPinnedMessage !== message) {
            setPinnedState(auxiliaryPinnedMessage, isPinned);
        }
    }

    cacheResolvedReferenceMessages(messages: IMessageReferenceData[]): void {
        (messages || []).forEach((message: IMessageReferenceData) => {
            if (!message?.id) {
                return;
            }

            const existingMessage = this._getResolvedReferenceMessageById(message.id);

            if (existingMessage) {
                const existingIndex = this.resolvedReferenceMessages.indexOf(existingMessage);

                this.resolvedReferenceMessages.splice(existingIndex, 1, message);
                return;
            }

            this.resolvedReferenceMessages.push(message);
        });
    }

    private _getPinnedMessageById(id: string | number): IMessageReferenceData | null {
        return this._getMessageFromCollectionById(this.pinnedMessages, id);
    }

    private _getResolvedReferenceMessageById(id: string | number): IMessageReferenceData | null {
        return this._getMessageFromCollectionById(this.resolvedReferenceMessages, id);
    }

    private _getMessageFromCollectionById(collection: IMessageReferenceData[], id: string | number): IMessageReferenceData | null {
        if (!id) {
            return null;
        }

        for (let index = 0; index < collection.length; index++) {
            if (String(collection[index].id) === String(id)) {
                return collection[index];
            }
        }

        return null;
    }
}

class RemoteRangeBookkeeping {
    private startIndex = 0;
    private initialized = false;

    reset(): void {
        this.startIndex = 0;
        this.initialized = false;
    }

    setStart(startIndex: number): void {
        this.startIndex = Math.max(0, startIndex);
        this.initialized = true;
    }

    sync(totalCount: number, loadedCount: number): { startIndex: number; endIndex: number } {
        this.setStart(Math.max(0, totalCount - loadedCount));

        return this.getRange(loadedCount);
    }

    getRange(loadedCount: number): { startIndex: number; endIndex: number } {
        const startIndex = this.initialized ? this.startIndex : 0;

        return {
            startIndex,
            endIndex: startIndex + loadedCount
        };
    }
}

/**
 * DataManager handles all data operations for the Chat component.
 * Manages message storage, retrieval, updates, and data source configuration.
 *
 * This is an internal collaborator - not a shared service.
 */
export class DataManager {
    /** Default configuration options */
    private static readonly DEFAULT_OPTIONS: IDataManagerOptions = {
        autoSync: true,
        schema: {
            model: {
                id: "id",
                fields: {
                    id: { type: "string" },
                    text: { type: "string" },
                    authorId: { type: "string" },
                    authorName: { type: "string" },
                    authorImageUrl: { type: "string" },
                    authorImageAltText: { type: "string" },
                    replyToId: { type: "string", defaultValue: null },
                    isDeleted: { type: "boolean", defaultValue: false },
                    isPinned: { type: "boolean", defaultValue: false },
                    isTyping: { type: "boolean", defaultValue: false },
                    timestamp: { type: "date" },
                    files: { parse: (value: any) => value || [] },
                    suggestedActions: { parse: (value: any) => value || [] },
                    status: { type: "string", defaultValue: null },
                    failed: { type: "boolean", defaultValue: false },
                    attachments: { parse: (value: any) => value || null },
                    attachmentLayout: { type: "string", defaultValue: null }
                }
            }
        }
    };

    /** Field mapping from chat options to data source fields */
    private static readonly FIELD_MAP: Record<string, string> = {
        text: "textField",
        authorId: "authorIdField",
        authorName: "authorNameField",
        authorImageUrl: "authorImageUrlField",
        authorImageAltText: "authorImageAltTextField",
        id: "idField",
        timestamp: "timestampField",
        files: "filesField",
        suggestedActions: "suggestedActionsField",
        replyToId: "replyToIdField",
        isDeleted: "isDeletedField",
        isPinned: "isPinnedField",
        isTyping: "isTypingField",
        status: "statusField",
        failed: "failedField",
        attachments: "attachmentsField",
        attachmentLayout: "attachmentLayoutField"
    };

    private dataSource: any; // kendo.data.DataSource
    private options: IDataManagerOptions;
    private context: IChatDataContext;
    private auxiliaryReferences: AuxiliaryReferenceStore;
    private remoteRange: RemoteRangeBookkeeping;

    /**
     * Constructs a new DataManager instance.
     * @param context - Context with chat options and services
     */
    constructor(context: IChatDataContext) {
        this.context = context;
        this.options = this.buildOptions(context.chatOptions);
        this.dataSource = this.createDataSource(context.chatOptions);
        this.auxiliaryReferences = new AuxiliaryReferenceStore(context.chatOptions.pinnedMessages || []);
        this.remoteRange = new RemoteRangeBookkeeping();
    }

    private _isRemoteDataSource(): boolean {
        return !!this.dataSource?.options?.serverPaging;
    }

    private _setPinnedState(message: IMessageReferenceData | null, isPinned: boolean): void {
        if (!message) {
            return;
        }

        if (typeof (message as any).set === "function") {
            (message as any).set("isPinned", isPinned);
            return;
        }

        (message as any).isPinned = isPinned;
    }

    /**
     * Builds internal options from chat options.
     */
    private buildOptions(chatOptions: Partial<IChatOptions>): IDataManagerOptions {
        let options: IDataManagerOptions;

        if (chatOptions.dataSource instanceof kendo.data.DataSource) {
            options = $.extend(true, {}, DataManager.DEFAULT_OPTIONS, chatOptions.dataSource.options);
        } else if (Array.isArray(chatOptions.dataSource)) {
            options = $.extend(true, {}, DataManager.DEFAULT_OPTIONS, { data: chatOptions.dataSource });
        } else {
            options = $.extend(true, {}, DataManager.DEFAULT_OPTIONS, chatOptions.dataSource);
        }

        options.autoAssignId = chatOptions.autoAssignId;
        this.mapFields(options.schema.model.fields, chatOptions);

        return options;
    }

    /**
     * Creates and configures a data source for the chat component.
     */
    private createDataSource(chatOptions: Partial<IChatOptions>): any {
        if (chatOptions.dataSource instanceof kendo.data.DataSource) {
            return chatOptions.dataSource;
        }

        return kendo.data.DataSource.create(this.options);
    }

    /**
     * Maps field configuration from chat options to data source field definitions.
     */
    private mapFields(
        fields: Record<string, any>,
        chatOptions: Partial<IChatOptions>
    ): void {
        for (const key in fields) {
            if (Object.prototype.hasOwnProperty.call(fields, key) && DataManager.FIELD_MAP[key]) {
                const optionKey = DataManager.FIELD_MAP[key] as keyof IChatOptions;
                fields[key].from = chatOptions[optionKey];
            }
        }
    }

    /**
     * Gets the data source instance.
     */
    getDataSource(): any {
        return this.dataSource;
    }

    /**
     * Gets the current data source view.
     */
    getView(): IMessage[] {
        return this.dataSource.view();
    }

    /**
     * Gets the last message in the data source view.
     */
    getLastMessage(): IMessage | null {
        const view = this.getView();
        return view.length ? view[view.length - 1] : null;
    }

    /**
     * Gets a message by ID from the data source.
     */
    getMessageById(id: string | number): IMessageReferenceData | null {
        if (!id) {
            return null;
        }

        return this.getLoadedMessageById(id) || this.getAuxiliaryMessageById(id);
    }

    getAuxiliaryMessageById(id: string | number): IMessageReferenceData | null {
        if (!id) {
            return null;
        }

        return this.auxiliaryReferences.getMessageById(id);
    }

    cacheResolvedReferenceMessages(messages: IMessageReferenceData[]): void {
        this.auxiliaryReferences.cacheResolvedReferenceMessages(messages);
    }

    getLoadedMessageById(id: string | number): IMessage | null {
        if (!id) {
            return null;
        }

        return this.dataSource.get(id);
    }

    /**
     * Gets a message by UID from the data source.
     */
    getMessageByUid(uid: string): IMessage | null {
        return this.dataSource.getByUid(uid);
    }

    /**
     * Gets a file by UID from a message's files array.
     */
    getFileByUid(message: IMessage, uid: string): IFile | null {
        if (!message || !uid) {
            return null;
        }
        return message.files.find(file => file.uid === uid) || null;
    }

    /**
     * Gets the currently pinned message.
     */
    getPinnedMessage(): IMessageReferenceData | null {
        return this.dataSource.data().find((m: IMessage) => m.isPinned) || this.auxiliaryReferences.getPinnedMessage() || null;
    }

    /**
     * Adds a new message to the data source.
     */
    postMessage(message: string | Partial<IMessage>, currentUserId: string): IMessage {
        const messageInput = typeof message === "string" ? { text: message } : message;

        const messageData: Partial<IMessage> = {
            id: this.options.autoAssignId ? utilsService.guid() : undefined,
            timestamp: new Date(),
            files: [],
            ...messageInput,
            authorId: messageInput.authorId?.toString() || currentUserId
        };

        return this.dataSource.add(messageData);
    }

    /**
     * Updates a message in the data source.
     */
    updateMessage(message: IMessage | Partial<IMessage>, newData: Partial<IMessage>): IMessage | null {
        if (!message) {
            return null;
        }

        let targetMessage = message;
        if (!(message instanceof kendo.data.ObservableObject)) {
            targetMessage = this.getLoadedMessageById((message as IMessage).id)!;
        }

        if (!targetMessage) {
            return null;
        }

        for (const key in newData) {
            if (Object.prototype.hasOwnProperty.call(newData, key)) {
                (targetMessage as any).set(key, (newData as any)[key]);
            }
        }

        return targetMessage as IMessage;
    }

    /**
     * Marks a message as deleted.
     */
    removeMessage(message: IMessage): boolean {
        if (!message) {
            return false;
        }

        (message as any).set("isDeleted", true);
        return true;
    }

    /**
     * Pins a message.
     */
    pinMessage(message: IMessage): IMessage | false {
        if (!message) {
            return false;
        }

        const pinnedMessage = this.getPinnedMessage();
        if (pinnedMessage && String(pinnedMessage.id) !== String(message.id)) {
            this._setPinnedState(pinnedMessage, false);
            this.auxiliaryReferences.syncPinnedMessageState(pinnedMessage, false, this._setPinnedState.bind(this));
        }

        this._setPinnedState(message, true);
        this.auxiliaryReferences.syncPinnedMessageState(message, true, this._setPinnedState.bind(this));

        return message;
    }

    /**
     * Clears the currently pinned message.
     */
    clearPinnedMessage(): void {
        const pinnedMessage = this.getPinnedMessage();
        this._setPinnedState(pinnedMessage, false);
        this.auxiliaryReferences.syncPinnedMessageState(pinnedMessage, false, this._setPinnedState.bind(this));
    }

    resetRemoteRange(): void {
        this.remoteRange.reset();
    }

    setRemoteRangeStart(startIndex: number): void {
        this.remoteRange.setStart(startIndex);
    }

    syncRemoteRange(totalCount = this.getTotalCount()): { startIndex: number; endIndex: number } {
        if (!this._isRemoteDataSource()) {
            this.resetRemoteRange();
            return this.getLoadedRange();
        }

        return this.remoteRange.sync(totalCount, this.getLoadedCount());
    }

    getLoadedCount(): number {
        return this.dataSource.data().length;
    }

    getLoadedRange(): { startIndex: number; endIndex: number } {
        const loadedCount = this.getLoadedCount();

        if (!this._isRemoteDataSource()) {
            return {
                startIndex: 0,
                endIndex: loadedCount
            };
        }

        return this.remoteRange.getRange(loadedCount);
    }

    isRangeLoaded(startIndex: number, endIndex: number): boolean {
        const loadedRange = this.getLoadedRange();

        return startIndex >= loadedRange.startIndex && endIndex <= loadedRange.endIndex;
    }

    getMessagesInRange(startIndex: number, endIndex: number): IMessage[] {
        const data = this.dataSource.data();
        const loadedRange = this.getLoadedRange();
        const safeStart = Math.max(startIndex, loadedRange.startIndex);
        const safeEnd = Math.min(endIndex, loadedRange.endIndex);
        const result: IMessage[] = [];

        if (safeEnd <= safeStart) {
            return result;
        }

        const localStart = safeStart - loadedRange.startIndex;
        const localEnd = safeEnd - loadedRange.startIndex;

        for (let i = localStart; i < localEnd; i++) {
            result.push(data[i]);
        }

        return result;
    }

    getMessageIndexById(id: string | number): number {
        if (!id) {
            return -1;
        }

        const data = this.dataSource.data();
        const loadedRange = this.getLoadedRange();

        for (let i = 0; i < data.length; i++) {
            if (String(data[i].id) === String(id)) {
                return loadedRange.startIndex + i;
            }
        }

        return -1;
    }

    getTotalCount(): number {
        if (this._isRemoteDataSource()) {
            return this.dataSource.total();
        }

        return this.dataSource.data().length;
    }
}
